// utils/noteFrequencies.ts
// Calculate note frequencies for guitar fretboard

import { NOTES, STRING_TUNING_NOTE_INDICES } from './constants';
import type { Note } from '../presets/presetSchema';

// ================================================================================
// FREQUENCY CONSTANTS
// ================================================================================

// A4 = 440 Hz is the standard tuning reference
const A4_FREQUENCY = 440 as const;
const A4_NOTE_INDEX = 9 as const; // A is at index 9 in our NOTES array

// Standard guitar tuning reference octaves for open strings
// E(0) = 4th octave, B(1) = 3rd octave, G(2) = 3rd octave, 
// D(3) = 3rd octave, A(4) = 2nd octave, E(5) = 2nd octave
const BASE_OCTAVES: readonly number[] = [4, 3, 3, 3, 2, 2] as const;

// ================================================================================
// TYPES
// ================================================================================

export interface NoteInfo {
  readonly name: Note;
  readonly index: number;
  readonly frequency: number;
}

// ================================================================================
// FREQUENCY CALCULATION FUNCTIONS
// ================================================================================

/**
 * Calculate the frequency of a note given its index (0-11)
 * @param noteIndex - Note index (0=C, 1=C#, 2=D, etc.)
 * @param octave - Octave number (4 is the standard reference octave)
 * @returns Frequency in Hz
 */
export function calculateNoteFrequency(noteIndex: number, octave: number = 4): number {
  // Validate inputs
  if (noteIndex < 0 || noteIndex > 11) {
    throw new Error(`Invalid note index: ${noteIndex}. Must be 0-11.`);
  }
  
  // Calculate semitone offset from A4
  const semitoneOffset = (octave - 4) * 12 + (noteIndex - A4_NOTE_INDEX);
  
  // Each semitone is a factor of 2^(1/12)
  return A4_FREQUENCY * Math.pow(2, semitoneOffset / 12);
}

/**
 * Get the note information for a specific string and fret
 * @param stringIndex - String index (0-5, where 0 is high E string)
 * @param fret - Fret number (0 = open string)
 * @returns Note information object with name, index, and frequency
 */
export function getNoteOnString(stringIndex: number, fret: number): NoteInfo {
  // Validate inputs
  if (stringIndex < 0 || stringIndex >= STRING_TUNING_NOTE_INDICES.length) {
    throw new Error(`Invalid string index: ${stringIndex}. Must be 0-${STRING_TUNING_NOTE_INDICES.length - 1}.`);
  }
  if (fret < 0) {
    throw new Error(`Invalid fret: ${fret}. Must be 0 or greater.`);
  }
  
  // Calculate the note index
  const noteIndex = (STRING_TUNING_NOTE_INDICES[stringIndex] + fret) % 12;
  const noteName = NOTES[noteIndex];
  
  // Calculate octave based on string and fret
  const openStringNoteIndex = STRING_TUNING_NOTE_INDICES[stringIndex];
  const totalSemitones = openStringNoteIndex + fret;
  const octave = BASE_OCTAVES[stringIndex] + Math.floor(totalSemitones / 12);
  
  // Calculate frequency
  const frequency = calculateNoteFrequency(noteIndex, octave);
  
  return {
    name: noteName,
    index: noteIndex,
    frequency: frequency
  };
}

/**
 * Get frequency for a note directly by string and fret position
 * @param stringIndex - String index (0-5)
 * @param fret - Fret number (0 = open string)
 * @returns Frequency in Hz
 */
export function getFrequencyForPosition(stringIndex: number, fret: number): number {
  const noteInfo = getNoteOnString(stringIndex, fret);
  return noteInfo.frequency;
}
